import Dialog from "@mui/material/Dialog";
import { ApplicationScreen, Consult } from "../../models";
import CircularProgress from "@mui/material/CircularProgress";
import { useState } from "react";
import { apiAccessRequest } from "../../constants";
import { BuildUrl } from "../../helpers";
import { useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { useCreateAuditLog } from "../../hooks";

interface CancelConsultModalProps {
	consult: Consult;
	open: boolean;
	onClose: (cancelled: boolean) => void;
}

export const CancelConsultModal = ({
	consult,
	open,
	onClose
}: CancelConsultModalProps) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { createAuditLog } = useCreateAuditLog();

	const callCreateAuditLog = async () => {
		try {
			await createAuditLog([
				{
					patientId: consult.patientId,
					patientName: `${consult.firstName} ${consult.lastName}`.trim(),
					clinicId: consult.clinicId,
					username: account?.name ?? "",
					applicationScreen: ApplicationScreen.AdminTelehealthQueue,
					action: "Cancelled consult"
				}
			]);
		} catch (error) {
			console.log("Error creating audit log", error);
		}
	};

	const handleConfirmClick = async () => {
		setIsError(false);
		setIsLoading(true);
		try {
			const requestUrl = BuildUrl(`Consult/${consult.id}/cancel?source=Admin`);
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				...(tokenResponse && {
					Authorization: `Bearer ${tokenResponse.accessToken}`
				})
			};
			const response = await fetch(requestUrl, {
				method: "POST",
				headers
			});

			if (!response.ok) {
				setIsError(true);
				return;
			}
			onClose(true);
			await callCreateAuditLog();
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			setIsError(true);
		}

		setIsLoading(false);
	};

	return (
		<Dialog open={open}>
			<div
				className="flex flex-col items-center p-5 gap-4"
				style={{ width: 420 }}
			>
				{!isError && (
					<>
						<p className="ch-m-text ch-em-text-navy text-center">
							Cancel Consult
						</p>
						<p className="pb-3 text-center">
							You are going to cancel the consult for {consult.firstName}{" "}
							{consult.lastName}
						</p>

						<div className="flex flex-col gap-4">
							{!isLoading && (
								<>
									<button
										className="ch-button ch-button-secondary justify-center"
										style={{ width: 280 }}
										onClick={() => handleConfirmClick()}
									>
										Cancel Consult
									</button>
									<button
										className="ch-button ch-button-primary justify-center"
										style={{ width: 280 }}
										onClick={() => onClose(false)}
									>
										Go Back
									</button>
								</>
							)}
							{isLoading && (
								<div className="flex justify-center py-3">
									<CircularProgress size="80px" />
								</div>
							)}
						</div>
					</>
				)}
				{isError && (
					<div className="flex flex-col items-center py-3">
						<img
							src="/images/svg/road-sign-warning.svg"
							height="50"
							alt="error icon"
						/>
						<p className="ch-m-text py-3 text-center">
							An error has occurred. The consult has not been cancelled. Try
							again later
						</p>
						<div className="flex gap-6">
							<button
								className="ch-button ch-button-primary"
								onClick={() => onClose(false)}
							>
								Go back
							</button>
						</div>
					</div>
				)}
			</div>
		</Dialog>
	);
};
